/**
 * PageLayout
 *
 * Phase 1 foundation component — the single, canonical page wrapper for every
 * module in Forge.  Enforces a consistent structure:
 *
 *   breadcrumbs (optional, ~16px)
 *   ┌──────────────────────────────────────────────────┐
 *   │  Title                              Actions slot │  ~40px
 *   └──────────────────────────────────────────────────┘
 *   content children                                      gap: --layout-content-gap
 *
 * Design goals:
 *   • ≤80px from the top of the content area to the first meaningful content
 *   • Max-width 1680px, centered, with responsive padding
 *   • Works for list pages, dashboards, detail pages, and settings
 *   • Respects existing density and spacing tokens
 *   • Drop-in replacement for ad-hoc header patterns during Phase 2 migration
 *
 * Usage:
 *   <PageLayout
 *     title="People"
 *     breadcrumbs={[{ label: "Home", href: "/" }]}
 *     actions={<Button>Add Person</Button>}
 *   >
 *     <CommunityTable />
 *   </PageLayout>
 *
 * Record (L2) pages use breadcrumbs too, ending in the record: L2 has no back arrow, the
 * breadcrumb is the back (docs/design/NAVIGATION_AND_STATES.md, PAGE_TEMPLATES.md § T2):
 *   <PageLayout
 *     title="Sarah Testington"
 *     subtitle="Member since Jan 2024"
 *     // Until the People overview lands, /people is the collection: never two crumbs to one place.
 *     breadcrumbs={[{ label: "People", href: "/people" }, { label: "Sarah Testington" }]}
 *     actions={<DropdownMenu>...</DropdownMenu>}
 *   >
 *     <RecordTabs />
 *   </PageLayout>
 *
 * `backButton` is for pages outside the L1 → L2 hierarchy that still need a way back (for
 * example a standalone utility page). Don't use it on record pages: it replaces the breadcrumbs.
 */

import { useCallback, useContext, useId, type ReactNode, type Ref } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { PageSubNavSlotContext, useClaimPageSubNavSlot } from "./pageSubNavSlot";
import { TopbarBreadcrumbSlotContext, TopbarBreadcrumbClaimContext, TOPBAR_BREADCRUMB_BREAKPOINT } from "./topbarBreadcrumbSlot";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PageBreadcrumbItem {
  label: string;
  href?: string;
  /** Router state for the link, e.g. a list's entry to put back (see `listReturnState`). */
  state?: unknown;
  icon?: LucideIcon;
}

export interface PageBackButton {
  /** Label shown next to the ← arrow */
  label?: string;
  /** Navigation path. Falls back to navigate(-1) when omitted. */
  href?: string;
  /** Router state for `href`. */
  state?: unknown;
}

export interface PageLayoutProps {
  /** Page title — rendered as an h1 */
  title: string;
  /** Optional subtitle below the title (e.g., record context, date range) */
  subtitle?: string;
  /** Breadcrumb trail above the title row */
  breadcrumbs?: PageBreadcrumbItem[];
  /** Back button for pages outside the L1 → L2 hierarchy. Not for record pages, which use
   *  breadcrumbs (L2 has no back arrow). If both are provided, backButton replaces the
   *  breadcrumbs. */
  backButton?: PageBackButton;
  /** Slot for action buttons / controls on the right side of the header row */
  actions?: ReactNode;
  /** Slot rendered between the title and the actions on the same row — useful
   *  for context chips, badges, or inline metadata */
  titleMeta?: ReactNode;
  /** Main page content */
  children: ReactNode;
  /** Override the max-width (defaults to --layout-max-width / 1680px) */
  maxWidth?: string;
  /** When true, content stretches full width (no max-width constraint).
   *  Useful for pages already handling their own width (e.g. SubLayout). */
  fluid?: boolean;
  /** Additional className on the outermost container */
  className?: string;
  /** When true, hides the title visually (still present for a11y via sr-only).
   *  Useful when a downstream component renders its own prominent heading. */
  hideTitle?: boolean;
  /** When true, the header row is omitted entirely (breadcrumbs/back still render) */
  headerless?: boolean;
  /** Ref to the h1. When set, the h1 takes tabIndex=-1 so a template can move focus to it
   *  on route change (docs/design/ACCESSIBILITY.md § Route change). */
  titleRef?: Ref<HTMLHeadingElement>;
  /** When true, the subtitle wraps instead of truncating to one line (the default,
   *  `line-clamp-1`). A T5 settings page's description states a required consequence
   *  ("applies org-wide and is audited") that must not be silently hidden on narrow screens
   *  by the default single-line clamp (Codex finding, PR #7013). */
  subtitleWrap?: boolean;
  /** When true, below 640px the breadcrumb trail collapses to one "‹ parent" link. Only an
   *  L2 record page opts in (SURFACES.md § Responsive); L1 and L3 trails stay whole. */
  phoneBackLink?: boolean;
  /** Shared migrated templates opt in; standalone PageLayout retains body breadcrumbs. */
  topbarBreadcrumbs?: boolean;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PageBreadcrumbs({ items, phoneBackLink, topbar = false, claimRef }: { items: PageBreadcrumbItem[]; phoneBackLink: boolean; topbar?: boolean; claimRef?: Ref<HTMLElement> }) {
  // On phones an L2 shows "‹ Collection" in place of the trail (SURFACES.md § Responsive): the
  // nearest linked crumb before the current page. Same landmark, one 44px link.
  const isPhone = useIsMobile(640);
  if (!items.length) return null;

  const parent = items.slice(0, -1).reverse().find((item) => item.href);
  if (phoneBackLink && isPhone && parent) {
    return (
      <nav aria-label="Breadcrumb" className="text-[13px] leading-tight">
        <Link
          to={parent.href}
          state={parent.state}
          className="-ml-2 inline-flex min-h-11 items-center gap-0.5 px-2 font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{parent.label}</span>
        </Link>
      </nav>
    );
  }

  return (
    <nav
      ref={claimRef}
      aria-label="Breadcrumb"
      className={cn("flex items-center gap-1 text-[13px] leading-tight text-muted-foreground", topbar ? "min-w-0 w-full flex-nowrap" : "flex-wrap")}
    >
      {items.map((item, index) => {
        const Icon = item.icon;
        const content = (
          <span className={cn("inline-flex items-center gap-1", topbar && "min-w-0 w-full")}>
            {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
            <span className={topbar ? "min-w-0 truncate" : undefined}>{item.label}</span>
          </span>
        );

        return (
            <span key={`${item.label}-${index}`} className={cn("inline-flex items-center gap-1", topbar && (index === items.length - 1 ? "min-w-0 flex-1" : "min-w-11 lg:min-w-6 shrink max-w-[35%]"))}>
            {item.href ? (
              <Link
                to={item.href}
                state={item.state}
                aria-label={topbar ? item.label : undefined}
                title={topbar ? item.label : undefined}
                className={cn("font-medium text-muted-foreground transition-colors hover:text-foreground", topbar && "flex min-w-11 min-h-11 items-center rounded px-1 py-2 lg:min-w-6 lg:min-h-6 focus-visible:ring-inset focus-visible:ring-offset-0")}
              >
                {topbar && index > 0 && index < items.length - 1 ? <><span aria-hidden="true" className="xl:hidden">…</span><span className="hidden min-w-0 flex-1 xl:block">{content}</span></> : <span className={topbar ? "block min-w-0 flex-1" : undefined}>{content}</span>}
              </Link>
            ) : (
              <span title={topbar ? item.label : undefined} className={cn("font-medium text-foreground", topbar && "block min-w-0 truncate")}>{content}</span>
            )}
            {index < items.length - 1 && (
              <span className="mx-0.5 text-muted-foreground/50" aria-hidden="true">
                /
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
}

function PageBackNav({ backButton }: { backButton: PageBackButton }) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (backButton.href) {
      navigate(backButton.href, { state: backButton.state });
    } else {
      navigate(-1);
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      className="-ml-2 gap-1.5 text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-3.5 w-3.5" />
      {backButton.label ?? "Back"}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function PageLayout({
  title,
  subtitle,
  breadcrumbs,
  backButton,
  actions,
  titleMeta,
  children,
  maxWidth,
  fluid = false,
  className,
  hideTitle = false,
  headerless = false,
  titleRef,
  subtitleWrap = false,
  phoneBackLink = false,
  topbarBreadcrumbs = false,
}: PageLayoutProps) {
  const breadcrumbHost = useContext(TopbarBreadcrumbSlotContext);
  const claim = useContext(TopbarBreadcrumbClaimContext);
  const claimId = useId();
  const claimRef = useCallback((node: HTMLElement | null) => { claim?.(claimId, node !== null); }, [claim, claimId]);
  const belowTopbar = useIsMobile(TOPBAR_BREADCRUMB_BREAKPOINT);
  const inTopbar = topbarBreadcrumbs && !backButton && Boolean(breadcrumbs?.length) && !belowTopbar && breadcrumbHost !== null;
  const hasNavRow = backButton || (breadcrumbs && breadcrumbs.length > 0);
  const showHeaderRow = !headerless && (title || actions || titleMeta);
  // A module host's phone section selector, which sits under the title. A headerless layout
  // draws no title, so its child header (PageHeader) claims the slot instead.
  // A hidden title (hideTitle) is no title either: the visible one is a child header's.
  const drawsTitle = Boolean(showHeaderRow) && !hideTitle;
  const subNavSlot = useClaimPageSubNavSlot(drawsTitle);

  return (
    <TopbarBreadcrumbSlotContext.Provider value={inTopbar ? null : breadcrumbHost}>
    <TopbarBreadcrumbClaimContext.Provider value={inTopbar ? null : claim}>
      {inTopbar && createPortal(<PageBreadcrumbs items={breadcrumbs} phoneBackLink={false} topbar claimRef={claimRef} />, breadcrumbHost)}
    <div
      className={cn(
        "mx-auto w-full",
        !fluid && "max-w-[var(--layout-max-width)]",
        className,
      )}
      style={maxWidth && !fluid ? { maxWidth } : undefined}
    >
      {/* Navigation row: breadcrumbs or back button */}
      {hasNavRow && !inTopbar && (
        <div className="mb-[var(--layout-header-gap)]">
          {backButton ? (
            <PageBackNav backButton={backButton} />
          ) : breadcrumbs && breadcrumbs.length > 0 ? (
            <PageBreadcrumbs items={breadcrumbs} phoneBackLink={phoneBackLink} />
          ) : null}
        </div>
      )}

      {/* Header row: title + actions */}
      {showHeaderRow && (
        <div
          className={cn(
            "flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between",
            "mb-[var(--layout-content-gap)]",
          )}
        >
          {/* Title block */}
          <div className="min-w-0 flex-1">
            <h1
              ref={titleRef}
              tabIndex={titleRef ? -1 : undefined}
              data-page-arrival={titleRef ? "template" : undefined}
              className={cn(
                "text-[var(--layout-title-size)] font-semibold leading-tight tracking-tight text-foreground",
                hideTitle && "sr-only",
              )}
            >
              {title}
            </h1>
            {subtitle && !hideTitle && (
              <p
                className={cn(
                  "mt-0.5 text-[13px] text-muted-foreground",
                  subtitleWrap ? "whitespace-normal" : "line-clamp-1",
                )}
              >
                {subtitle}
              </p>
            )}
            {titleMeta && !hideTitle && (
              <div className="mt-1 flex flex-wrap items-center gap-2">{titleMeta}</div>
            )}
          </div>

          {/* Actions */}
          {actions && (
            <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-2">
              {actions}
            </div>
          )}
        </div>
      )}

      {subNavSlot ? <div className="mb-[var(--layout-content-gap)]">{subNavSlot}</div> : null}

      {/* Page content */}
      <div className="space-y-[var(--density-page-gap)]">
        {/* Claimed above (or unclaimed by a headerless layout): children mustn't draw it twice. */}
        {drawsTitle ? <PageSubNavSlotContext.Provider value={null}>{children}</PageSubNavSlotContext.Provider> : children}
      </div>
    </div>
    </TopbarBreadcrumbClaimContext.Provider>
    </TopbarBreadcrumbSlotContext.Provider>
  );
}
